import React, { useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';

const LABEL_W = 100;
const PAD_R = 60;
const HEADER_H = 60;
const ROW_H = 240;
const DIAMOND = 9;
const CARD_H = 32;
const LANE_PAD_Y = 10;
const CARD_LEVEL_GAP = 38;
const CARD_MIN_GAP_X = 10;
const STEM_GAP = 8;
const CARD_MIN_W = 120;
const CARD_MAX_W = 220;
const CARD_TEXT_X_PAD = 10;
const CARD_STATUS_SLOT_W = 20;
const CARD_TEXT_RIGHT_PAD = 10;
const CARD_FONT_SIZE = 12;
const CARD_FONT_WEIGHT = '500';
const ELLIPSIS = '...';
const DAY_MS = 24 * 60 * 60 * 1000;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const appendStatusMarker = (group, centerX, centerY, status, color) => {
  const ns = 'http://www.w3.org/2000/svg';

  if (status === 'doing') {
    const triangle = document.createElementNS(ns, 'polygon');
    triangle.setAttribute(
      'points',
      `${centerX},${centerY - 4.5} ${centerX + 4.5},${centerY + 3.5} ${centerX - 4.5},${centerY + 3.5}`
    );
    triangle.setAttribute('fill', 'none');
    triangle.setAttribute('stroke', color);
    triangle.setAttribute('stroke-width', '1.6');
    group.appendChild(triangle);
    return;
  }

  if (status === 'done') {
    const check = document.createElementNS(ns, 'path');
    check.setAttribute('d', `M ${centerX - 4} ${centerY} L ${centerX - 1} ${centerY + 3} L ${centerX + 4} ${centerY - 3}`);
    check.setAttribute('fill', 'none');
    check.setAttribute('stroke', color);
    check.setAttribute('stroke-width', '2');
    check.setAttribute('stroke-linecap', 'round');
    check.setAttribute('stroke-linejoin', 'round');
    group.appendChild(check);
    return;
  }

  // todo: hollow circle
  const circle = document.createElementNS(ns, 'circle');
  circle.setAttribute('cx', centerX);
  circle.setAttribute('cy', centerY);
  circle.setAttribute('r', '3.8');
  circle.setAttribute('fill', 'none');
  circle.setAttribute('stroke', color);
  circle.setAttribute('stroke-width', '1.6');
  group.appendChild(circle);
};

const getStatusLabelKey = (status) => {
  if (status === 'doing') return 'tools.timeline.statusDoing';
  if (status === 'done') return 'tools.timeline.statusDone';
  return 'tools.timeline.statusTodo';
};

const Canvas = forwardRef(({ nodes, lanes, laneOrder, axisConfig, onNodeClick, onNodeDateChange, onCanvasBlankClick }, ref) => {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);
  const dragStateRef = useRef({ active: false, moved: false });

  const startDate = new Date(axisConfig?.startDate || '2026-03-01');
  const endDate = new Date(axisConfig?.endDate || '2026-06-01');
  const totalMs = Math.max(24 * 60 * 60 * 1000, endDate - startDate);
  const months = Array.isArray(axisConfig?.months) ? axisConfig.months : [];
  const gridDensity = axisConfig?.gridDensity || 'auto';
  const nodeStyle = axisConfig?.nodeStyle || 'tag';

  const datePct = (dateStr) => {
    const date = new Date(dateStr);
    return Math.max(0, Math.min(1, (date - startDate) / totalMs));
  };

  const dateFromPct = (pct) => {
    const ms = startDate.getTime() + pct * totalMs;
    const date = new Date(ms);
    return date.toISOString().slice(0, 10);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      P0: '#ef4444',
      P1: '#f59e0b',
      P2: '#6b7280',
      P3: '#d1d5db',
    };
    return colors[priority] || colors.P1;
  };

  const resolveSvgCssVars = useCallback((svgEl) => {
    if (!svgEl) {
      return;
    }

    const rootStyles = getComputedStyle(document.documentElement);
    const resolveVarValue = (raw) => {
      if (!raw || typeof raw !== 'string' || !raw.includes('var(')) {
        return raw;
      }

      return raw.replace(/var\((--[^)\s]+)\)/g, (_, varName) => {
        const val = rootStyles.getPropertyValue(varName)?.trim();
        return val || raw;
      });
    };

    const attrs = ['fill', 'stroke', 'color'];
    const allNodes = [svgEl, ...svgEl.querySelectorAll('*')];
    allNodes.forEach((node) => {
      attrs.forEach((attr) => {
        const current = node.getAttribute(attr);
        if (current && current.includes('var(')) {
          node.setAttribute(attr, resolveVarValue(current));
        }
      });

      const inlineStyle = node.getAttribute('style');
      if (inlineStyle && inlineStyle.includes('var(')) {
        node.setAttribute('style', resolveVarValue(inlineStyle));
      }
    });
  }, []);

  useImperativeHandle(ref, () => ({
    exportSVG: () => {
      if (!svgRef.current) return;
      const serializer = new XMLSerializer();
      const clonedSvg = svgRef.current.cloneNode(true);
      clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      resolveSvgCssVars(clonedSvg);
      const svgStr = serializer.serializeToString(clonedSvg);
      const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timeline_${new Date().toISOString().slice(0, 10)}.svg`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(t('tools.timeline.exported'));
    },
    exportPNG: () => {
      if (!svgRef.current) {
        return;
      }

      const sourceSvg = svgRef.current;
      const width = Math.max(1, Math.round(Number(sourceSvg.getAttribute('width')) || sourceSvg.clientWidth || 1200));
      const height = Math.max(1, Math.round(Number(sourceSvg.getAttribute('height')) || sourceSvg.clientHeight || 800));

      const serializer = new XMLSerializer();
      const clonedSvg = sourceSvg.cloneNode(true);
      clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      resolveSvgCssVars(clonedSvg);
      const svgContent = `<?xml version="1.0" standalone="no"?>\n${serializer.serializeToString(clonedSvg)}`;
      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      const image = new Image();
      image.decoding = 'sync';

      image.onload = () => {
        try {
          const dpr = window.devicePixelRatio || 1;
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(width * dpr);
          canvas.height = Math.round(height * dpr);

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('Canvas context unavailable');
          }

          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.drawImage(image, 0, 0, width, height);

          canvas.toBlob((pngBlob) => {
            URL.revokeObjectURL(svgUrl);

            if (!pngBlob) {
              message.error(t('tools.timeline.pngExportFailed'));
              return;
            }

            const downloadUrl = URL.createObjectURL(pngBlob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `timeline_${new Date().toISOString().slice(0, 10)}.png`;
            a.click();
            URL.revokeObjectURL(downloadUrl);
            message.success(t('tools.timeline.exported'));
          }, 'image/png');
        } catch (error) {
          URL.revokeObjectURL(svgUrl);
          console.error('PNG export failed:', error);
          message.error(t('tools.timeline.pngExportFailed'));
        }
      };

      image.onerror = (error) => {
        URL.revokeObjectURL(svgUrl);
        console.error('Failed to load SVG for PNG export:', error);
        message.error(t('tools.timeline.pngExportFailed'));
      };

      image.src = svgUrl;
    },
  }), [t, resolveSvgCssVars]);

  const renderTimeline = useCallback(() => {
    if (!svgRef.current || !containerRef.current) return;

    const hideTooltip = () => {
      if (!tooltipRef.current) return;
      tooltipRef.current.style.display = 'none';
    };

    const showTooltip = (event, text) => {
      if (!tooltipRef.current || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const x = event.clientX - containerRect.left + containerRef.current.scrollLeft + 12;
      const y = event.clientY - containerRect.top + containerRef.current.scrollTop + 12;

      tooltipRef.current.textContent = text;
      tooltipRef.current.style.left = `${x}px`;
      tooltipRef.current.style.top = `${y}px`;
      tooltipRef.current.style.display = 'block';
    };

    const availW = Math.max(containerRef.current.clientWidth || 0, 320);
    const trackW = availW - LABEL_W - PAD_R;
    const totalH = HEADER_H + laneOrder.length * ROW_H + 20;
    const timelineTop = HEADER_H;
    const timelineBottom = HEADER_H + laneOrder.length * ROW_H;

    svgRef.current.setAttribute('width', availW);
    svgRef.current.setAttribute('height', totalH);
    svgRef.current.setAttribute('viewBox', `0 0 ${availW} ${totalH}`);

    const xOf = (pct) => LABEL_W + pct * trackW;
    const pctFromX = (x) => clamp((x - LABEL_W) / trackW, 0, 1);
    const startMs = startDate.getTime();
    const endMs = endDate.getTime();
    const pxPerDay = trackW / Math.max(1, totalMs / DAY_MS);
    let showWeeklyGrid = false;
    let showDailyGrid = false;

    if (gridDensity === 'day') {
      showWeeklyGrid = true;
      showDailyGrid = true;
    } else if (gridDensity === 'week') {
      showWeeklyGrid = true;
    } else if (gridDensity === 'month') {
      showWeeklyGrid = false;
      showDailyGrid = false;
    } else {
      // auto
      showWeeklyGrid = pxPerDay >= 4;
      showDailyGrid = pxPerDay >= 22;
    }

    const monthTicks = months
      .map((m) => ({ ...m, ms: new Date(m.date).getTime() }))
      .filter((m) => Number.isFinite(m.ms) && m.ms >= startMs && m.ms < endMs)
      .sort((a, b) => a.ms - b.ms);

    const textWidthCache = new Map();

    const measureTextWidth = (text, options = {}) => {
      const { fontSize = CARD_FONT_SIZE, fontWeight = CARD_FONT_WEIGHT } = options;
      if (!svgRef.current) return 0;
      const cacheKey = `${fontWeight}|${fontSize}|${text}`;
      if (textWidthCache.has(cacheKey)) {
        return textWidthCache.get(cacheKey);
      }

      const probe = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      probe.setAttribute('x', '-9999');
      probe.setAttribute('y', '-9999');
      probe.setAttribute('font-size', String(fontSize));
      probe.setAttribute('font-weight', String(fontWeight));
      probe.textContent = text;
      svgRef.current.appendChild(probe);
      const measured = probe.getComputedTextLength();
      svgRef.current.removeChild(probe);

      textWidthCache.set(cacheKey, measured);
      return measured;
    };

    const truncateToWidth = (text, maxWidth, options = {}) => {
      if (!text) return '';
      if (measureTextWidth(text, options) <= maxWidth) {
        return text;
      }

      const chars = Array.from(text);
      let low = 0;
      let high = chars.length;

      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        const candidate = `${chars.slice(0, mid).join('')}${ELLIPSIS}`;
        if (measureTextWidth(candidate, options) <= maxWidth) {
          low = mid;
        } else {
          high = mid - 1;
        }
      }

      return `${chars.slice(0, low).join('')}${ELLIPSIS}`;
    };

    const wrapTextToWidth = (text, maxWidth, options = {}) => {
      const { fontSize = 13, fontWeight = '600', maxLines = 3 } = options;
      const source = (text || '').trim();
      if (!source) {
        return [''];
      }

      const byWords = source.includes(' ');
      const tokens = byWords ? source.split(/\s+/).filter(Boolean) : Array.from(source);
      const lines = [];
      let line = '';

      const appendToken = (current, token) => {
        if (!current) return token;
        return byWords ? `${current} ${token}` : `${current}${token}`;
      };

      tokens.forEach((token) => {
        const candidate = appendToken(line, token);
        if (measureTextWidth(candidate, { fontSize, fontWeight }) <= maxWidth) {
          line = candidate;
          return;
        }

        if (line) {
          lines.push(line);
          line = '';
        }

        if (measureTextWidth(token, { fontSize, fontWeight }) <= maxWidth) {
          line = token;
          return;
        }

        // Fallback for a single very long token.
        const pieces = Array.from(token);
        let pieceLine = '';
        pieces.forEach((ch) => {
          const pieceCandidate = `${pieceLine}${ch}`;
          if (measureTextWidth(pieceCandidate, { fontSize, fontWeight }) <= maxWidth) {
            pieceLine = pieceCandidate;
          } else {
            if (pieceLine) {
              lines.push(pieceLine);
            }
            pieceLine = ch;
          }
        });
        line = pieceLine;
      });

      if (line) {
        lines.push(line);
      }

      if (lines.length <= maxLines) {
        return lines;
      }

      const sliced = lines.slice(0, maxLines);
      sliced[maxLines - 1] = truncateToWidth(sliced[maxLines - 1], maxWidth, { fontSize, fontWeight });
      return sliced;
    };

    // Clear SVG
    while (svgRef.current.firstChild) {
      svgRef.current.removeChild(svgRef.current.firstChild);
    }

    // Define arrow markers for axis lines (one per lane for color matching)
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    laneOrder.forEach((catKey) => {
      const lane = lanes[catKey];
      if (!lane) return;

      const marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker');
      marker.setAttribute('id', `arrowhead-${catKey}`);
      marker.setAttribute('markerWidth', '10');
      marker.setAttribute('markerHeight', '7');
      marker.setAttribute('refX', '9');
      marker.setAttribute('refY', '3.5');
      marker.setAttribute('orient', 'auto');
      const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      polygon.setAttribute('points', '0 0, 10 3.5, 0 7');
      polygon.setAttribute('fill', lane.border);
      marker.appendChild(polygon);
      defs.appendChild(marker);
    });
    svgRef.current.appendChild(defs);

    // Background
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', availW);
    bg.setAttribute('height', totalH);
    bg.setAttribute('fill', 'var(--bg-root)');
    svgRef.current.appendChild(bg);

    // Header background
    const headerBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    headerBg.setAttribute('x', 0);
    headerBg.setAttribute('y', 0);
    headerBg.setAttribute('width', availW);
    headerBg.setAttribute('height', HEADER_H);
    headerBg.setAttribute('fill', 'var(--bg-surface)');
    svgRef.current.appendChild(headerBg);

    // Month labels and dividers
    monthTicks.forEach((m, i) => {
      const x1 = xOf(datePct(m.date));
      const x2 = i < monthTicks.length - 1 ? xOf(datePct(monthTicks[i + 1].date)) : xOf(1);
      const cx = (x1 + x2) / 2;

      const monthBand = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      monthBand.setAttribute('x', x1);
      monthBand.setAttribute('y', HEADER_H);
      monthBand.setAttribute('width', Math.max(0, x2 - x1));
      monthBand.setAttribute('height', laneOrder.length * ROW_H);
      monthBand.setAttribute('fill', i % 2 === 0 ? 'var(--bg-surface)' : 'var(--bg-root)');
      monthBand.setAttribute('opacity', i % 2 === 0 ? '0.2' : '0.08');
      svgRef.current.appendChild(monthBand);

      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', cx);
      text.setAttribute('y', HEADER_H - 12);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', '14');
      text.setAttribute('font-weight', '600');
      text.setAttribute('fill', 'var(--text-secondary)');
      text.textContent = m.label;
      svgRef.current.appendChild(text);

      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', x1);
      line.setAttribute('y1', timelineTop);
      line.setAttribute('x2', x1);
      line.setAttribute('y2', timelineBottom);
      line.setAttribute('stroke', 'var(--text-muted)');
      line.setAttribute('stroke-width', '1.6');
      line.setAttribute('stroke-dasharray', '10,8');
      line.setAttribute('stroke-linecap', 'round');
      line.setAttribute('opacity', '0.6');
      svgRef.current.appendChild(line);
    });

    const headerHit = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    headerHit.setAttribute('x', LABEL_W);
    headerHit.setAttribute('y', 0);
    headerHit.setAttribute('width', trackW);
    headerHit.setAttribute('height', HEADER_H);
    headerHit.setAttribute('fill', 'transparent');
    svgRef.current.appendChild(headerHit);

    if (monthTicks.length > 0) {
      // Month headers rendered. Blank double-click handling is attached on svg root below.
    }

    // Lanes
    laneOrder.forEach((catKey, ri) => {
      const lane = lanes[catKey];
      if (!lane) {
        return;
      }
      const y = HEADER_H + ri * ROW_H;
      const midY = y + ROW_H / 2;

      // Lane background
      const laneBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      laneBg.setAttribute('x', LABEL_W);
      laneBg.setAttribute('y', y);
      laneBg.setAttribute('width', trackW);
      laneBg.setAttribute('height', ROW_H);
      laneBg.setAttribute('fill', lane.gridBg);
      laneBg.setAttribute('opacity', '0.6');
      svgRef.current.appendChild(laneBg);

      // In day-grid mode, highlight weekends so users can quickly distinguish workdays vs weekends.
      if (showDailyGrid) {
        for (let ms = startMs; ms < endMs; ms += DAY_MS) {
          const dayOfWeek = new Date(ms).getUTCDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          if (!isWeekend) {
            continue;
          }

          const nextMs = Math.min(ms + DAY_MS, endMs);
          const xStart = xOf((ms - startMs) / totalMs);
          const xEnd = xOf((nextMs - startMs) / totalMs);
          const weekendFill = 'rgba(107, 114, 128, 0.1)';
          const weekendBorder = 'rgba(107, 114, 128, 0.2)';

          const weekendBand = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          weekendBand.setAttribute('x', xStart);
          weekendBand.setAttribute('y', y);
          weekendBand.setAttribute('width', Math.max(0, xEnd - xStart));
          weekendBand.setAttribute('height', ROW_H);
          weekendBand.setAttribute('fill', weekendFill);
          weekendBand.setAttribute('opacity', '1');
          svgRef.current.appendChild(weekendBand);

          const weekendEdge = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          weekendEdge.setAttribute('x1', xStart);
          weekendEdge.setAttribute('y1', y);
          weekendEdge.setAttribute('x2', xStart);
          weekendEdge.setAttribute('y2', y + ROW_H);
          weekendEdge.setAttribute('stroke', weekendBorder);
          weekendEdge.setAttribute('stroke-width', '1');
          weekendEdge.setAttribute('opacity', '0.5');
          svgRef.current.appendChild(weekendEdge);
        }
      }

      // Minor grids are drawn per lane above lane background, so they remain visible.
      if (showDailyGrid || showWeeklyGrid) {
        for (let ms = startMs + DAY_MS; ms < endMs; ms += DAY_MS) {
          const tickDate = new Date(ms);
          const isMonday = tickDate.getUTCDay() === 1;
          const x = xOf((ms - startMs) / totalMs);

          if (isMonday && showWeeklyGrid) {
            const weekLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            weekLine.setAttribute('x1', x);
            weekLine.setAttribute('y1', y);
            weekLine.setAttribute('x2', x);
            weekLine.setAttribute('y2', y + ROW_H);
            weekLine.setAttribute('stroke', 'var(--border-accent)');
            weekLine.setAttribute('stroke-width', '1');
            weekLine.setAttribute('stroke-dasharray', '3,7');
            weekLine.setAttribute('opacity', '0.55');
            svgRef.current.appendChild(weekLine);
            continue;
          }

          if (showDailyGrid) {
            const dayLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            dayLine.setAttribute('x1', x);
            dayLine.setAttribute('y1', y);
            dayLine.setAttribute('x2', x);
            dayLine.setAttribute('y2', y + ROW_H);
            dayLine.setAttribute('stroke', 'var(--border-subtle)');
            dayLine.setAttribute('stroke-width', '1');
            dayLine.setAttribute('opacity', '0.6');
            svgRef.current.appendChild(dayLine);
          }
        }
      }

      // Lane label
      const labelBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      labelBg.setAttribute('x', 0);
      labelBg.setAttribute('y', y);
      labelBg.setAttribute('width', LABEL_W);
      labelBg.setAttribute('height', ROW_H);
      labelBg.setAttribute('fill', 'var(--bg-surface)');
      svgRef.current.appendChild(labelBg);

      const labelText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      labelText.setAttribute('x', LABEL_W / 2);
      labelText.setAttribute('text-anchor', 'middle');
      labelText.setAttribute('font-size', '13');
      labelText.setAttribute('font-weight', '600');
      labelText.setAttribute('fill', lane.color);

      const labelLines = wrapTextToWidth(lane.name, LABEL_W - 12, {
        fontSize: 13,
        fontWeight: '600',
        maxLines: 3,
      });
      const lineHeight = 15;
      const firstY = midY - ((labelLines.length - 1) * lineHeight) / 2;

      labelLines.forEach((textLine, idx) => {
        const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        tspan.setAttribute('x', LABEL_W / 2);
        tspan.setAttribute('y', String(firstY + idx * lineHeight));
        tspan.textContent = textLine;
        labelText.appendChild(tspan);
      });

      svgRef.current.appendChild(labelText);

      // Axis line with arrow
      const axisLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      axisLine.setAttribute('x1', LABEL_W);
      axisLine.setAttribute('y1', midY);
      axisLine.setAttribute('x2', xOf(1));
      axisLine.setAttribute('y2', midY);
      axisLine.setAttribute('stroke', lane.border);
      axisLine.setAttribute('stroke-width', '1.5');
      axisLine.setAttribute('stroke-dasharray', '4,3');
      axisLine.setAttribute('marker-end', `url(#arrowhead-${catKey})`);
      svgRef.current.appendChild(axisLine);

      // Row divider
      const divider = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      divider.setAttribute('x1', 0);
      divider.setAttribute('y1', y + ROW_H);
      divider.setAttribute('x2', availW);
      divider.setAttribute('y2', y + ROW_H);
      divider.setAttribute('stroke', 'var(--border)');
      divider.setAttribute('stroke-width', '1');
      svgRef.current.appendChild(divider);

      // Nodes in this lane
      const laneNodes = nodes.filter((n) => n.cat === catKey).sort((a, b) => new Date(a.date) - new Date(b.date));

      const upperLastRight = [];
      const lowerLastRight = [];

      const maxLevels = Math.max(
        1,
        Math.floor((ROW_H / 2 - DIAMOND - STEM_GAP - LANE_PAD_Y - CARD_H) / CARD_LEVEL_GAP) + 1
      );

      const pickLevel = (lastRightArr, left, right) => {
        for (let level = 0; level < maxLevels; level += 1) {
          const prevRight = lastRightArr[level] ?? Number.NEGATIVE_INFINITY;
          if (left >= prevRight + CARD_MIN_GAP_X) {
            lastRightArr[level] = right;
            return level;
          }
        }

        // Fallback: reuse the level with the smallest right edge when nodes are too dense.
        let bestLevel = 0;
        let bestRight = lastRightArr[0] ?? Number.POSITIVE_INFINITY;
        for (let i = 1; i < maxLevels; i += 1) {
          const candidate = lastRightArr[i] ?? Number.POSITIVE_INFINITY;
          if (candidate < bestRight) {
            bestRight = candidate;
            bestLevel = i;
          }
        }
        lastRightArr[bestLevel] = Math.max(bestRight, right);
        return bestLevel;
      };

      laneNodes.forEach((node, idx) => {
        const xCenter = xOf(datePct(node.date));
        const useAbove = idx % 2 === 0;

        // Diamond
        const diamond = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        diamond.setAttribute(
          'points',
          `${xCenter},${midY - DIAMOND} ${xCenter + DIAMOND},${midY} ${xCenter},${midY + DIAMOND} ${xCenter - DIAMOND},${midY}`
        );
        diamond.setAttribute('fill', lane.bg);
        diamond.setAttribute('stroke', lane.color);
        diamond.setAttribute('stroke-width', '2');
        diamond.setAttribute('data-node-interactive', 'true');
        diamond.style.cursor = 'grab';

        const tooltipText = [
          `${t('tools.timeline.nodeName')}: ${node.label || '-'}`,
          `${t('tools.timeline.date')}: ${node.date || '-'}`,
          `${t('tools.timeline.lane')}: ${lane.name || '-'}`,
          `${t('tools.timeline.priority')}: ${node.priority || '-'}`,
          `${t('tools.timeline.status')}: ${t(getStatusLabelKey(node.status))}`,
        ].join('\n');
        svgRef.current.appendChild(diamond);

        // Node card
        const priorityColor = getPriorityColor(node.priority);
        const isFish = nodeStyle === 'fish';
        const isRocket = nodeStyle === 'rocket';
        const isLeaf = nodeStyle === 'leaf';
        const textLeftPad = isFish
          ? 32
          : isRocket
            ? 34
            : isLeaf
              ? 24
              : CARD_TEXT_X_PAD;
        const textRightReserve = isFish
          ? 30
          : isRocket
            ? 34
            : isLeaf
              ? 30
              : (CARD_TEXT_RIGHT_PAD + CARD_STATUS_SLOT_W);

        const maxTextWidth = Math.max(24, CARD_MAX_W - textLeftPad - textRightReserve);
        const displayText = truncateToWidth(node.label || '', maxTextWidth);
        const textWidth = measureTextWidth(displayText);
        const cardWidth = clamp(
          textWidth + textLeftPad + textRightReserve,
          CARD_MIN_W,
          CARD_MAX_W
        );

        let cardX = xCenter - cardWidth / 2;
        const minCardX = LABEL_W + 4;
        const maxCardX = xOf(1) - cardWidth - 4;
        cardX = clamp(cardX, minCardX, maxCardX);

        const cardLeft = cardX;
        const cardRight = cardX + cardWidth;
        const level = useAbove
          ? pickLevel(upperLastRight, cardLeft, cardRight)
          : pickLevel(lowerLastRight, cardLeft, cardRight);

        const laneTop = y + LANE_PAD_Y;
        const laneBottom = y + ROW_H - LANE_PAD_Y;

        let cardY;
        if (useAbove) {
          const desiredY = midY - DIAMOND - STEM_GAP - CARD_H - level * CARD_LEVEL_GAP;
          cardY = clamp(desiredY, laneTop, midY - DIAMOND - STEM_GAP - CARD_H);
        } else {
          const desiredY = midY + DIAMOND + STEM_GAP + level * CARD_LEVEL_GAP;
          cardY = clamp(desiredY, midY + DIAMOND + STEM_GAP, laneBottom - CARD_H);
        }

        const stemY1 = useAbove ? midY - DIAMOND - 1 : midY + DIAMOND + 1;
        const stemY2 = useAbove ? cardY + CARD_H : cardY;

        // Stem
        const stem = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        stem.setAttribute('x1', xCenter);
        stem.setAttribute('y1', stemY1);
        stem.setAttribute('x2', xCenter);
        stem.setAttribute('y2', stemY2);
        stem.setAttribute('stroke', lane.color);
        stem.setAttribute('stroke-width', '1.5');
        stem.setAttribute('opacity', '0.5');
        svgRef.current.appendChild(stem);

        const cardGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        cardGroup.setAttribute('data-node-interactive', 'true');
        cardGroup.style.cursor = 'grab';

        const tipWidth = isFish
          ? 16
          : isRocket
            ? 14
            : 8;
        const cardPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');

        const pathData = isFish
          ? [
            // Fish style: forked tail on the left, rounded head on the right.
            `M ${cardX + tipWidth} ${cardY}`,
            `Q ${cardX + 11} ${cardY + CARD_H * 0.28} ${cardX} ${cardY + CARD_H * 0.18}`,
            `L ${cardX + 8} ${cardY + CARD_H / 2}`,
            `L ${cardX} ${cardY + CARD_H * 0.82}`,
            `Q ${cardX + 11} ${cardY + CARD_H * 0.72} ${cardX + tipWidth} ${cardY + CARD_H}`,
            `L ${cardX + cardWidth - 14} ${cardY + CARD_H}`,
            `Q ${cardX + cardWidth - 1} ${cardY + CARD_H - 1} ${cardX + cardWidth - 1} ${cardY + CARD_H / 2}`,
            `Q ${cardX + cardWidth - 1} ${cardY + 1} ${cardX + cardWidth - 14} ${cardY}`,
            'Z',
          ].join(' ')
          : isRocket
            ? [
              // Rocket style: rounded body with a sharper right-facing nose.
              `M ${cardX + 18} ${cardY}`,
              `Q ${cardX + 8} ${cardY + 1} ${cardX + 8} ${cardY + CARD_H / 2}`,
              `Q ${cardX + 8} ${cardY + CARD_H - 1} ${cardX + 18} ${cardY + CARD_H}`,
              `L ${cardX + cardWidth - tipWidth} ${cardY + CARD_H}`,
              `L ${cardX + cardWidth} ${cardY + CARD_H / 2}`,
              `L ${cardX + cardWidth - tipWidth} ${cardY}`,
              'Z',
            ].join(' ')
            : isLeaf
              ? [
                // Comet style: smooth body with a bright right-facing head.
                `M ${cardX + 10} ${cardY + 4}`,
                `Q ${cardX + 2} ${cardY + CARD_H / 2} ${cardX + 10} ${cardY + CARD_H - 4}`,
                `L ${cardX + cardWidth - 14} ${cardY + CARD_H - 2}`,
                `Q ${cardX + cardWidth + 1} ${cardY + CARD_H / 2} ${cardX + cardWidth - 14} ${cardY + 2}`,
                'Z',
              ].join(' ')
              : [
                // Tag style: square left edge with right-side pointed tip.
                `M ${cardX} ${cardY}`,
                `L ${cardX + cardWidth - tipWidth} ${cardY}`,
                `L ${cardX + cardWidth} ${cardY + CARD_H / 2}`,
                `L ${cardX + cardWidth - tipWidth} ${cardY + CARD_H}`,
                `L ${cardX} ${cardY + CARD_H}`,
                'Z',
              ].join(' ');

        cardPath.setAttribute('d', pathData);
        cardPath.setAttribute('fill', lane.bg);
        cardPath.setAttribute('stroke', lane.border);
        cardPath.setAttribute('stroke-width', '1.5');
        cardGroup.appendChild(cardPath);

        if (isFish) {
          const fishFin = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          // Keep fin aligned with the node stem (xCenter), while avoiding head/tail overlap.
          const finCenterX = clamp(xCenter, cardX + tipWidth + 10, cardX + cardWidth - 28);
          fishFin.setAttribute(
            'd',
            [
              `M ${finCenterX - 9} ${cardY + 1}`,
              `Q ${finCenterX} ${cardY - 8} ${finCenterX + 9} ${cardY + 1}`,
              'Z',
            ].join(' ')
          );
          fishFin.setAttribute('fill', lane.border);
          fishFin.setAttribute('opacity', '0.7');
          cardGroup.appendChild(fishFin);

          const fishEyeWhite = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          fishEyeWhite.setAttribute('cx', String(cardX + cardWidth - 11));
          fishEyeWhite.setAttribute('cy', String(cardY + CARD_H / 2 - 1));
          fishEyeWhite.setAttribute('r', '3');
          fishEyeWhite.setAttribute('fill', '#ffffff');
          fishEyeWhite.setAttribute('stroke', lane.border);
          fishEyeWhite.setAttribute('stroke-width', '0.9');
          fishEyeWhite.setAttribute('opacity', '0.95');
          cardGroup.appendChild(fishEyeWhite);

          const fishEyePupil = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          fishEyePupil.setAttribute('cx', String(cardX + cardWidth - 10.3));
          fishEyePupil.setAttribute('cy', String(cardY + CARD_H / 2 - 1));
          fishEyePupil.setAttribute('r', '1.2');
          fishEyePupil.setAttribute('fill', lane.color);
          cardGroup.appendChild(fishEyePupil);
        }

        if (isRocket) {
          const rocketFinTop = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          rocketFinTop.setAttribute('d', `M ${cardX + 16} ${cardY + 7} L ${cardX + 7} ${cardY + 2} L ${cardX + 16} ${cardY + 2} Z`);
          rocketFinTop.setAttribute('fill', lane.border);
          rocketFinTop.setAttribute('opacity', '0.85');
          cardGroup.appendChild(rocketFinTop);

          const rocketFinBottom = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          rocketFinBottom.setAttribute('d', `M ${cardX + 16} ${cardY + CARD_H - 7} L ${cardX + 7} ${cardY + CARD_H - 3} L ${cardX + 16} ${cardY + CARD_H - 3} Z`);
          rocketFinBottom.setAttribute('fill', lane.border);
          rocketFinBottom.setAttribute('opacity', '0.85');
          cardGroup.appendChild(rocketFinBottom);

          const rocketFlameOuter = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          rocketFlameOuter.setAttribute(
            'd',
            [
              `M ${cardX + 8} ${cardY + CARD_H / 2}`,
              `Q ${cardX + 1} ${cardY + CARD_H * 0.24} ${cardX + 4} ${cardY + CARD_H / 2}`,
              `Q ${cardX + 1} ${cardY + CARD_H * 0.76} ${cardX + 8} ${cardY + CARD_H / 2}`,
              'Z',
            ].join(' ')
          );
          rocketFlameOuter.setAttribute('fill', priorityColor);
          rocketFlameOuter.setAttribute('opacity', '0.8');
          cardGroup.appendChild(rocketFlameOuter);

          const rocketFlameInner = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          rocketFlameInner.setAttribute(
            'd',
            [
              `M ${cardX + 8} ${cardY + CARD_H / 2}`,
              `Q ${cardX + 4} ${cardY + CARD_H * 0.36} ${cardX + 5.5} ${cardY + CARD_H / 2}`,
              `Q ${cardX + 4} ${cardY + CARD_H * 0.64} ${cardX + 8} ${cardY + CARD_H / 2}`,
              'Z',
            ].join(' ')
          );
          rocketFlameInner.setAttribute('fill', '#ffffff');
          rocketFlameInner.setAttribute('opacity', '0.85');
          cardGroup.appendChild(rocketFlameInner);

          const rocketStripe = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          rocketStripe.setAttribute('x1', String(cardX + cardWidth - 30));
          rocketStripe.setAttribute('y1', String(cardY + 5));
          rocketStripe.setAttribute('x2', String(cardX + cardWidth - 30));
          rocketStripe.setAttribute('y2', String(cardY + CARD_H - 5));
          rocketStripe.setAttribute('stroke', lane.border);
          rocketStripe.setAttribute('stroke-width', '1');
          rocketStripe.setAttribute('opacity', '0.45');
          cardGroup.appendChild(rocketStripe);
        }

        if (isLeaf) {
          const cometSparkBig = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          cometSparkBig.setAttribute('cx', String(cardX + 9));
          cometSparkBig.setAttribute('cy', String(cardY + CARD_H / 2));
          cometSparkBig.setAttribute('r', '2.2');
          cometSparkBig.setAttribute('fill', lane.border);
          cometSparkBig.setAttribute('opacity', '0.7');
          cardGroup.appendChild(cometSparkBig);

          const cometSparkSmall = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          cometSparkSmall.setAttribute('cx', String(cardX + 5));
          cometSparkSmall.setAttribute('cy', String(cardY + CARD_H / 2 + 1.2));
          cometSparkSmall.setAttribute('r', '1.2');
          cometSparkSmall.setAttribute('fill', lane.border);
          cometSparkSmall.setAttribute('opacity', '0.6');
          cardGroup.appendChild(cometSparkSmall);
        }

        const priorityBar = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        const priorityX = isFish || isRocket || isLeaf
          ? cardX + cardWidth - 22
          : cardX;
        const priorityY = isFish || isRocket || isLeaf
          ? cardY + 4
          : cardY;
        const priorityW = isFish || isRocket || isLeaf ? 3 : 4;
        const priorityH = isFish || isRocket || isLeaf ? CARD_H - 8 : CARD_H;
        priorityBar.setAttribute('x', String(priorityX));
        priorityBar.setAttribute('y', String(priorityY));
        priorityBar.setAttribute('width', String(priorityW));
        priorityBar.setAttribute('height', String(priorityH));
        if (isFish || isRocket || isLeaf) {
          priorityBar.setAttribute('rx', '1.5');
        }
        priorityBar.setAttribute('fill', priorityColor);
        cardGroup.appendChild(priorityBar);

        const cardText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        cardText.setAttribute('x', String(cardX + textLeftPad));
        cardText.setAttribute('y', cardY + 20);
        cardText.setAttribute('font-size', String(CARD_FONT_SIZE));
        cardText.setAttribute('font-weight', CARD_FONT_WEIGHT);
        cardText.setAttribute('fill', lane.color);
        cardText.textContent = displayText;
        cardGroup.appendChild(cardText);

        const statusX = isFish
          ? cardX + tipWidth + 4
          : isRocket
            ? cardX + 20
            : isLeaf
              ? cardX + tipWidth + 4
              : cardX + cardWidth - 10;
        appendStatusMarker(cardGroup, statusX, cardY + CARD_H / 2, node.status || 'todo', lane.color);

        const attachNodeDrag = (target) => {
          target.addEventListener('mousedown', (event) => {
            hideTooltip();
            dragStateRef.current = {
              active: true,
              moved: false,
              nodeId: node.id,
              startX: event.clientX,
            };
            target.style.cursor = 'grabbing';
          });
        };

        const attachTooltip = (target) => {
          target.addEventListener('mouseenter', (event) => {
            showTooltip(event, tooltipText);
          });
          target.addEventListener('mousemove', (event) => {
            showTooltip(event, tooltipText);
          });
          target.addEventListener('mouseleave', hideTooltip);
        };

        attachNodeDrag(cardGroup);
        attachNodeDrag(diamond);
        attachTooltip(cardGroup);
        attachTooltip(diamond);

        svgRef.current.appendChild(cardGroup);
      });
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (today.getTime() >= startMs && today.getTime() < endMs) {
      const todayX = xOf((today.getTime() - startMs) / totalMs);

      const todayLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      todayLine.setAttribute('x1', todayX);
      todayLine.setAttribute('y1', timelineTop);
      todayLine.setAttribute('x2', todayX);
      todayLine.setAttribute('y2', timelineBottom);
      todayLine.setAttribute('stroke', 'var(--accent)');
      todayLine.setAttribute('stroke-width', '2');
      todayLine.setAttribute('stroke-dasharray', '6,5');
      todayLine.setAttribute('opacity', '0.9');
      svgRef.current.appendChild(todayLine);

      const todayLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      todayLabel.setAttribute('x', todayX + 6);
      todayLabel.setAttribute('y', 18);
      todayLabel.setAttribute('font-size', '11');
      todayLabel.setAttribute('font-weight', '700');
      todayLabel.setAttribute('fill', 'var(--accent)');
      todayLabel.textContent = t('tools.timeline.today');
      svgRef.current.appendChild(todayLabel);
    }

    const handleMouseMove = (event) => {
      if (!dragStateRef.current.active) {
        return;
      }
      if (Math.abs(event.clientX - dragStateRef.current.startX) > 3) {
        dragStateRef.current.moved = true;
      }
    };

    const handleMouseUp = (event) => {
      if (!dragStateRef.current.active) {
        if (!event || event.type === 'mouseleave') {
          hideTooltip();
        }
        return;
      }

      const dragged = dragStateRef.current;
      dragStateRef.current = { active: false, moved: false };

      if (!dragged.moved) {
        const clickNode = nodes.find((n) => n.id === dragged.nodeId);
        if (clickNode) {
          onNodeClick(clickNode);
        }
        return;
      }

      if (!onNodeDateChange || !svgRef.current) {
        return;
      }

      const rect = svgRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const nextDate = dateFromPct(pctFromX(x));
      onNodeDateChange(dragged.nodeId, nextDate);
    };

    svgRef.current.onmousemove = handleMouseMove;
    svgRef.current.onmouseup = handleMouseUp;
    svgRef.current.onmouseleave = handleMouseUp;
  }, [
    nodes,
    lanes,
    laneOrder,
    months,
    gridDensity,
    onNodeClick,
    onNodeDateChange,
    onCanvasBlankClick,
    startDate,
    endDate,
    totalMs,
  ]);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const update = () => {
      renderTimeline();
    };

    update();

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        update();
      });
      observer.observe(containerRef.current);
    }

    window.addEventListener('resize', update);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', update);
    };
  }, [renderTimeline]);

  const handleContainerDoubleClick = useCallback((event) => {
    if (!onCanvasBlankClick || !svgRef.current || !containerRef.current) {
      return;
    }

    // Check if double-click is on a node
    const target = event.target;
    if (target.hasAttribute && target.hasAttribute('data-node-interactive')) {
      return;
    }

    // Check if double-click is on a parent of a node
    let element = target;
    while (element && element !== containerRef.current) {
      if (element.hasAttribute && element.hasAttribute('data-node-interactive')) {
        return;
      }
      element = element.parentElement;
    }

    const svgRect = svgRef.current.getBoundingClientRect();
    const x = event.clientX - svgRect.left;
    const y = event.clientY - svgRect.top;

    // Check if click is in the track area
    const trackW = Math.max(containerRef.current.clientWidth - LABEL_W - PAD_R, 0);
    const xEnd = LABEL_W + trackW;
    if (x < LABEL_W || x > xEnd) {
      return;
    }

    // Determine which lane was clicked
    let clickedCat = null;
    if (y < HEADER_H) {
      clickedCat = laneOrder[0] || null;
    } else {
      const laneIndex = Math.floor((y - HEADER_H) / ROW_H);
      if (laneIndex >= 0 && laneIndex < laneOrder.length) {
        clickedCat = laneOrder[laneIndex];
      }
    }

    if (clickedCat) {
      const currentTotalMs = Math.max(DAY_MS, endDate.getTime() - startDate.getTime());
      const pct = Math.max(0, Math.min(1, (x - LABEL_W) / trackW));
      const ms = startDate.getTime() + pct * currentTotalMs;
      const date = new Date(ms);

      onCanvasBlankClick({
        date: date.toISOString().slice(0, 10),
        cat: clickedCat,
      });
    }
  }, [onCanvasBlankClick, laneOrder, startDate, endDate]);

  return (
    <div ref={containerRef} className={styles.canvasWrap}>
      <svg ref={svgRef} className={styles.svg} onDoubleClick={handleContainerDoubleClick} />
      <div ref={tooltipRef} className={styles.tooltip} />
    </div>
  );
});

Canvas.displayName = 'Canvas';

export default Canvas;
